import React, { Suspense, lazy, useCallback, useMemo } from 'react';
import { Alert, Box, Spinner, Stack, Text } from '@chakra-ui/react';
import { useColorModeValue } from '../../components/ui/color-mode';

const StockPriceHistoryRechartsShell = lazy(() =>
  import('recharts').then(({
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine,
  }) => ({
    default: function StockPriceHistoryRechartsShell({
      chartData,
      gridStroke,
      axisColor,
      formatPriceTick,
      formatDateTick,
      tooltipRenderer,
      referenceDate,
      referenceLabel,
      referenceTimestamp,
      xDomain,
    }) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey="dateMs"
              type="number"
              scale="time"
              domain={xDomain}
              tickFormatter={formatDateTick}
              tick={{ fontSize: 12, fill: axisColor }}
              tickMargin={8}
              minTickGap={28}
            />
            <YAxis
              dataKey="price"
              tickFormatter={formatPriceTick}
              tick={{ fontSize: 12, fill: axisColor }}
              tickMargin={6}
              width={68}
              domain={['auto', 'auto']}
            />
            <Tooltip content={tooltipRenderer} />
            {referenceTimestamp ? (
              <ReferenceLine
                x={referenceTimestamp}
                stroke="#E53E3E"
                strokeWidth={2}
                strokeDasharray="4 4"
                label={{
                  value: referenceLabel || 'Snapshot',
                  position: 'insideTopRight',
                  fill: '#E53E3E',
                  fontSize: 12,
                }}
              />
            ) : null}
            <Line
              type="monotone"
              dataKey="price"
              name="Stock Price"
              stroke="#2C7A7B"
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      );
    },
  })),
);

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function parseDateMs(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) {
    return null;
  }

  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatDateTick(value) {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) {
    return '';
  }

  return new Date(timestamp).toISOString().slice(0, 10);
}

const PRICE_TICK_WHOLE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const PRICE_TICK_DECIMAL_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

function formatPriceTick(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '';
  }

  if (Math.abs(numericValue) >= 1000) {
    return `$${Math.round(numericValue).toLocaleString()}`;
  }

  return (
    numericValue >= 100 ? PRICE_TICK_WHOLE_FORMATTER : PRICE_TICK_DECIMAL_FORMATTER
  ).format(numericValue);
}

function formatVolume(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Math.round(numericValue).toLocaleString('en-US');
}

const TOOLTIP_PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatTooltipPrice(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return TOOLTIP_PRICE_FORMATTER.format(numericValue);
}

function getErrorMessage(error) {
  if (!error) {
    return 'Unable to load stock price history.';
  }

  if (typeof error === 'string') {
    return error;
  }

  return error.message || 'Unable to load stock price history.';
}

function getStatusCopy(priceStatus, priceMessage, redactionEnabled) {
  if (priceStatus === 'partial') {
    return {
      label: 'Partial history',
      detail: priceMessage || 'Some prices in this range are unavailable.',
      tone: 'orange',
    };
  }

  if (priceStatus === 'unavailable') {
    return {
      label: redactionEnabled ? 'Pre-snapshot history unavailable' : 'Price history unavailable',
      detail: priceMessage || 'No usable prices were found for this range.',
      tone: 'gray',
    };
  }

  return null;
}

const EMPTY_PRICE_HISTORY = [];

export default function StockPriceHistoryChart({
  prices = EMPTY_PRICE_HISTORY,
  loading = false,
  error = null,
  redactionEnabled = false,
  preferAdjustedClose = true,
  priceStatus = null,
  priceMessage = null,
  referenceDate = null,
  referenceLabel = null,
}) {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const panelBg = useColorModeValue('white', 'gray.900');
  const emptyBg = useColorModeValue('gray.50', 'gray.800');
  const gridStroke = useColorModeValue('#E2E8F0', '#2D3748');
  const axisColor = useColorModeValue('#4A5568', '#CBD5E0');
  const tooltipBg = useColorModeValue('white', 'gray.800');
  const tooltipBorder = useColorModeValue('gray.200', 'gray.700');
  const tooltipText = useColorModeValue('gray.800', 'gray.100');
  const tooltipMuted = useColorModeValue('gray.600', 'gray.400');

  const chartData = useMemo(() => (
    (Array.isArray(prices) ? prices : [])
      .reduce((rows, price) => {
        const dateMs = parseDateMs(price?.trading_date);
        const adjustedClose = toFiniteNumber(price?.adj_close);
        const close = toFiniteNumber(price?.close);
        const plottedPrice = preferAdjustedClose && adjustedClose !== null ? adjustedClose : close;

        if (!price?.trading_date || dateMs === null || plottedPrice === null) {
          return rows;
        }

        rows.push({
          date: price?.trading_date,
          dateMs,
          price: plottedPrice,
          close,
          adj_close: adjustedClose,
          volume: price?.volume ?? null,
        });

        return rows;
      }, [])
  ), [preferAdjustedClose, prices]);
  const statusCopy = getStatusCopy(priceStatus, priceMessage, redactionEnabled);
  const firstChartDate = chartData[0]?.date;
  const lastChartDate = chartData[chartData.length - 1]?.date;
  const priceLabel = preferAdjustedClose ? 'Adjusted close' : 'Close';
  const referenceTimestamp = parseDateMs(referenceDate);
  const xDomain = useMemo(() => {
    const timestamps = chartData.map((price) => price.dateMs);
    if (referenceTimestamp !== null) {
      timestamps.push(referenceTimestamp);
    }

    if (!timestamps.length) {
      return ['dataMin', 'dataMax'];
    }

    return [Math.min(...timestamps), Math.max(...timestamps)];
  }, [chartData, referenceTimestamp]);

  const tooltipRenderer = useCallback(
    ({ active, payload }) => {
      if (!active || !payload?.length) return null;

      const point = payload[0]?.payload || {};
      const volume = formatVolume(point.volume);

      return (
        <Stack
          gap="1"
          bg={tooltipBg}
          border="1px solid"
          borderColor={tooltipBorder}
          borderRadius="md"
          boxShadow="lg"
          p="3"
          color={tooltipText}
        >
          <Text fontWeight="semibold">{point.date}</Text>
          {point.adj_close !== null && (
            <Box display="flex" justifyContent="space-between" gap="4">
              <Text fontSize="sm" color={tooltipMuted}>Adjusted close</Text>
              <Text fontSize="sm" fontWeight="semibold">{formatTooltipPrice(point.adj_close)}</Text>
            </Box>
          )}
          {point.close !== null && (
            <Box display="flex" justifyContent="space-between" gap="4">
              <Text fontSize="sm" color={tooltipMuted}>Closing price</Text>
              <Text fontSize="sm" fontWeight="semibold">{formatTooltipPrice(point.close)}</Text>
            </Box>
          )}
          {volume && (
            <Box display="flex" justifyContent="space-between" gap="4">
              <Text fontSize="sm" color={tooltipMuted}>Volume</Text>
              <Text fontSize="sm" fontWeight="semibold">{volume}</Text>
            </Box>
          )}
        </Stack>
      );
    },
    [tooltipBg, tooltipBorder, tooltipMuted, tooltipText],
  );

  if (loading) {
    return (
      <Box
        minH="320px"
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        bg={panelBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        data-testid="stock-price-history-loading"
      >
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert.Root status="error" borderRadius="md" data-testid="stock-price-history-error">
        <Alert.Indicator />
        <Alert.Content>{getErrorMessage(error)}</Alert.Content>
      </Alert.Root>
    );
  }

  if (!chartData.length) {
    return (
      <Box
        minH="220px"
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        bg={emptyBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        gap="1"
        px="4"
        textAlign="center"
        data-testid="stock-price-history-empty"
      >
        <Text color={mutedText} fontSize="sm" fontWeight="medium">
          {statusCopy?.label || (redactionEnabled
            ? 'No pre-snapshot price history'
            : 'No stock price history')}
        </Text>
        <Text color={mutedText} fontSize="xs">
          {statusCopy?.detail || 'No usable prices are available for this range.'}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      w="full"
      minH="320px"
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      bg={panelBg}
      p="3"
      data-testid="stock-price-history-chart"
    >
      <Stack
        direction={{ base: 'column', sm: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', sm: 'center' }}
        gap="1"
        mb="2"
      >
        <Text fontSize="sm" fontWeight="semibold" color={tooltipText}>
          {priceLabel}
        </Text>
        {firstChartDate && lastChartDate ? (
          <Text fontSize="xs" color={mutedText}>
            {firstChartDate} to {lastChartDate}
          </Text>
        ) : null}
      </Stack>
      {statusCopy ? (
        <Box
          borderWidth="1px"
          borderColor={statusCopy.tone === 'orange' ? 'orange.200' : borderColor}
          borderRadius="md"
          bg={statusCopy.tone === 'orange' ? 'orange.50' : emptyBg}
          px="3"
          py="2"
          mb="3"
          data-testid="stock-price-history-status"
        >
          <Text fontSize="xs" fontWeight="semibold" color={tooltipText}>
            {statusCopy.label}
          </Text>
          <Text fontSize="xs" color={mutedText}>
            {statusCopy.detail}
          </Text>
        </Box>
      ) : null}
      <Suspense
        fallback={
          <Box h={{ base: '260px', md: '320px' }} display="flex" alignItems="center" justifyContent="center">
            <Spinner size="lg" />
          </Box>
        }
      >
        <Box h={{ base: '260px', md: '320px' }}>
          <StockPriceHistoryRechartsShell
            chartData={chartData}
            gridStroke={gridStroke}
            axisColor={axisColor}
            formatPriceTick={formatPriceTick}
            formatDateTick={formatDateTick}
            tooltipRenderer={tooltipRenderer}
            referenceDate={referenceDate}
            referenceLabel={referenceLabel}
            referenceTimestamp={referenceTimestamp}
            xDomain={xDomain}
          />
        </Box>
      </Suspense>
    </Box>
  );
}
